import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useClassStore } from './useClassStore';
import { useProgramStore } from './useProgramStore';

interface User {
    userId: number;
    loginId: string;
    name: string;
    email: string;
    roleId: number;
    roleName: string;
    lastLogin: string;
}

interface AuthState {
    // User authentication
    user: User | null;
    token: string | null;
    refreshToken: string | null;
    isLoading: boolean;
    error: string | null;
    success: boolean;
    
    // Permissions and menus
    permissions: any[];
    menus: any[];
    userName: string;
    
    // Actions
    setUser: (user: User | null) => void;
    setToken: (token: string | null) => void;
    setRefreshToken: (refreshToken: string | null) => void;
    setLoading: (loading: boolean) => void;
    setError: (error: string | null) => void;
    setSuccess: (success: boolean) => void;
    setPermissions: (permissions: any[]) => void;
    setMenus: (menus: any[]) => void;
    setUserName: (userName: string) => void;
    logout: () => void;
    login: (user: User, token: string, refreshToken: string) => void;
}

export const useAuthStore = create<AuthState>()(
    persist(
        (set, get) => ({
            // Initial state
            user: null,
            token: null,
            refreshToken: null,
            isLoading: false,
            error: null,
            success: false,
            permissions: [],
            menus: [],
            userName: '',

            // Actions
            setUser: (user) => set({ user }),
            setToken: (token) => set({ token }),
            setRefreshToken: (refreshToken) => set({ refreshToken }),
            setLoading: (isLoading) => set({ isLoading }),
            setError: (error) => set({ error }),
            setSuccess: (success) => set({ success }),
            setPermissions: (permissions) => set({ permissions }),
            setMenus: (menus) => set({ menus }),
            setUserName: (userName) => set({ userName }),

            logout: () => {
                set({
                    user: null,
                    token: null,
                    refreshToken: null,
                    isLoading: false,
                    error: null,
                    success: false,
                    permissions: [],
                    menus: [],
                    userName: '',
                });
                // Clear localStorage
                localStorage.removeItem('user');
                localStorage.removeItem('token');
                localStorage.removeItem('refreshToken');
                localStorage.removeItem('app-permissions');
                localStorage.removeItem('app-menus');
                localStorage.removeItem('user-name');
                localStorage.removeItem('IDKS@ROLE');
                localStorage.removeItem('userId');
                
                // Reset class and program stores
                useClassStore.getState().reset();
                useProgramStore.getState().reset();
            },

            login: (user, token, refreshToken) => {
                set({
                    user,
                    token,
                    refreshToken,
                    isLoading: false,
                    error: null,
                    success: true,
                    userName: user.name,
                });
                
                // Store in localStorage
                localStorage.setItem('user', JSON.stringify(user));
                localStorage.setItem('token', token);
                localStorage.setItem('refreshToken', refreshToken);
                localStorage.setItem('user-name', user.name);
                localStorage.setItem('userId', user.userId.toString());
            },
        }),
        {
            name: 'auth-storage', // unique name for localStorage key
            partialize: (state) => ({
                user: state.user,
                token: state.token,
                refreshToken: state.refreshToken,
                permissions: state.permissions,
                menus: state.menus,
                userName: state.userName,
            }),
        }
    )
); 